import Modal from 'react-modal';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdClose } from 'react-icons/md';
import { TbArrowsMinimize, TbArrowsMaximize } from 'react-icons/tb';
import Button from 'components/Button';
import Loader from 'components/Loader';
import {
  assetActions,
  AssetAttributesInfo,
  AssetElementsInfo,
  EyeViDto,
} from 'actions/asset';
import AttributeRow from './attributeRow';
import ElementRow from './elementRow';
import { resolveAttributeDetails, type AttributesModalData, isAssetHubModalData } from './attributeResolvers';
import {
  SDM_DEFECT_FEATURE_CATEGORY,
  isSdmFeatureInfoDetails,
  toSdmFeatureInfoRows,
  type SdmFeatureInfoDetails,
} from 'domain/surveyDataManager/map/sdmFeatureInfo';
import {
  getModalDetailDisplayEntries,
  getAssetModalId,
  getModalGeometry,
  isAssetAttributeModalDetails,
  type AttributeModalDetails,
  type ModalDetailDisplayEntry,
  type ModalDetailsState,
} from './attributeDetailsTypes';
import {
  clampAttributesModalPositionX,
  readInitialAttributesModalPosition,
  type AttributesModalPosition,
} from './attributesModalDrag';
import Draggable from 'react-draggable';
import type { DraggableData, DraggableEvent } from 'react-draggable';
import * as ReactTabs from 'react-tabs';
import Iframe from 'react-iframe';
import 'react-tabs/style/react-tabs.css';
import './style.scss';
import AssetHub from './assetHub';
import { FiRefreshCw } from 'react-icons/fi';
import { getNextWindowZIndex } from 'utils/windowZIndex';

const { Tab, Tabs, TabList, TabPanel } = ReactTabs;

export type AttributesPanelData = AttributesModalData | SdmFeatureInfoDetails;

interface Props {
  isOpen: boolean;
  data: AttributesPanelData | null;
  closeModal: (title: string) => void;
  setIsExpanded: (isExpanded: boolean) => void;
  isExpanded: boolean;
  rightSidebarRef: React.RefObject<HTMLDivElement>;
}

const Attributes: React.FC<Props> = ({
  isOpen = false,
  setIsExpanded,
  data,
  closeModal,
  isExpanded,
  rightSidebarRef,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [detailsUnavailableMessage, setDetailsUnavailableMessage] = useState<
    string | null
  >(null);
  const [isModalMinimized, setModalMinimized] = useState<boolean>(false);
  const [isEVModalMinimized, setEVModalMinimized] = useState<boolean>(false);
  const [modalDetails, setModalDetails] = useState<ModalDetailsState>({});
  const [currentTab, setCurrentTab] = useState<number>(0);
  const changeTab = (index: number) => setCurrentTab(index);
  const [showAttributes, setShowAttributes] = useState<boolean>(false);
  const [attributes, setAttributes] = useState<AssetAttributesInfo[]>([]);
  const [elements, setElements] = useState<AssetElementsInfo[]>([]);
  const [showElements, setShowElements] = useState<boolean>(false);
  const [spans, setSpans] = useState<number[]>([]);
  const [selectedSpan, setSelectedSpan] = useState<number>(1);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [selectedAssetType, setSelectedAssetType] = useState<string>('');
  const [selectedEyeviYear, setSelectedEyeviYear] = useState<string>('uk2021');
  const [eyeViYears, setEyeViYears] = useState<number[]>([]);
  const [eyeViAssetTypes, setEyeViAssetTypes] = useState<string[]>([]);
  const [showEyeVi, setShowEyeVi] = useState<boolean>(false);
  const [eyeViData, setEyeViData] = useState<EyeViDto[]>([]);
  const [showAssetHub, setShowAssetHub] = useState<boolean>(false);
  const [attributesRefreshLoading, setAttributesRefreshLoading] =
    useState(false);
  const [wasAttributesRefreshedClicked, setWasAttributesRefreshedClicked] =
    useState(false);
  const [wasAssetHubRefreshedClicked, setWasAssetHubRefreshedClicked] =
    React.useState<boolean>(false);
  const [assetHubRefreshToken, setAssetHubRefreshToken] =
    React.useState<number>(0);
  const [wasAssetHubRefreshLoading, setWasAssetHubRefreshLoading] =
    React.useState<boolean>(false);
  const [zIndex, setZIndex] = useState(() => getNextWindowZIndex());
  const [navbarWidth, setNavbarWidth] = useState<number>(
    (document.querySelector('.xa-navbar.collapsed') as HTMLElement)
      ?.offsetWidth,
  );
  const [modalPosition, setModalPosition] = useState<AttributesModalPosition>(
    readInitialAttributesModalPosition,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(
    (document.querySelector('.xa-toolbar.collapsed') as HTMLElement)
      ?.offsetWidth ?? 0,
  );
  const modalHeight = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let formatedYear =
      selectedAssetType === 'FW'
        ? `uk${selectedYear}footway`
        : `uk${selectedYear}`;

    const lineCoordinates = (getModalGeometry(modalDetails) || '')
      .replace('LINESTRING', '')
      .replace('(', '')
      .replace(')', '')
      .trim()
      .split(',');
    let coordinates = [];
    if (data?.latitude && data?.longitude) {
      formatedYear = `${formatedYear}&lon=${data?.longitude}&lat=${data?.latitude}`;
    } else if (lineCoordinates.length > 0) {
      coordinates = lineCoordinates[0].split(' ');
      formatedYear = `${formatedYear}&lon=${coordinates[0]}&lat=${coordinates[1]}`;
    }

    setSelectedEyeviYear(formatedYear);
  }, [
    selectedYear,
    selectedAssetType,
    modalDetails,
    data?.latitude,
    data?.longitude,
  ]);

  const assetModalId = getAssetModalId(modalDetails);

  useEffect(() => {
    setCurrentTab(0);
    setShowAttributes(false);
    setShowElements(false);
    setModalDetails({});
    setShowAssetHub(false);
    setDetailsUnavailableMessage(null);
    setIsLoading(true);
  }, [data]);

  useEffect(() => {
    if (showAssetHub) {
      return;
    }
    if (
      showAttributes &&
      data?.category === 'asset' &&
      assetModalId !== undefined &&
      currentTab === 1
    ) {
      assetActions()
        .getAssetAttributes(assetModalId, wasAssetHubRefreshedClicked)
        .then((result) => {
          setAttributes(result);
          setDetailsUnavailableMessage(null);
        })
        .catch(() => {
          setAttributes([]);
          setDetailsUnavailableMessage('Unable to load attributes');
        });
    }
  }, [showAssetHub, showAttributes, currentTab, assetModalId, data?.category, wasAssetHubRefreshedClicked]);

  useEffect(() => {
    if (showAssetHub) {
      return;
    }
    if (
      showElements &&
      data?.category === 'asset' &&
      assetModalId !== undefined &&
      ((currentTab === 1 && !showAttributes) || currentTab === 2)
    ) {
      assetActions()
        .getAssetElements(assetModalId, wasAssetHubRefreshedClicked)
        .then((result) => {
          setElements(result);
          setDetailsUnavailableMessage(null);
        })
        .catch(() => {
          setElements([]);
          setDetailsUnavailableMessage('Unable to load elements');
        });
    }
  }, [
    showAssetHub,
    showElements,
    currentTab,
    wasAssetHubRefreshLoading,
    showAttributes,
    assetModalId,
    data?.category,
    wasAssetHubRefreshedClicked,
  ]);

  useEffect(() => {
    if (showAssetHub) {
      return;
    }
    if (showElements && data?.category === 'asset' && assetModalId !== undefined) {
      assetActions()
        .getSpans(assetModalId, wasAssetHubRefreshedClicked)
        .then((result) => {
          setSpans([...new Array(result)].map((x, y) => y + 1));
        })
        .catch(() => {
          setSpans([]);
          setDetailsUnavailableMessage('Unable to load spans');
        });
    }
  }, [
    showAssetHub,
    showElements,
    wasAssetHubRefreshLoading,
    assetModalId,
    data?.category,
    wasAssetHubRefreshedClicked,
  ]);

  const parseModalDetails = (modalDet: ModalDetailsState) => {
    setDetailsUnavailableMessage(null);
    setModalDetails(modalDet);
    if ('assetTypeCode' in modalDet) {
      setShowAttributes(Boolean(modalDet.attributes));
      setShowElements(Boolean(modalDet.elements));
    } else {
      setShowAttributes(false);
      setShowElements(false);
    }
    setIsLoading(false);
  };

  const parseEyeViYears = (years: EyeViDto[]) => {
    if ((years || []).length > 0) {
      setEyeViData(years);
      const assetType = years[0].assetType;
      setSelectedAssetType(assetType);
      const eyeViAssetYears = years
        .filter((x) => x.assetType === assetType)
        .map((x) => x.year);
      const eyeViAssetTypes = Array.from(
        new Set(years.map((x) => x.assetType)),
      );
      setEyeViYears(eyeViAssetYears);
      setEyeViAssetTypes(eyeViAssetTypes);
      if ((eyeViAssetYears || []).length > 0) {
        setSelectedYear(eyeViAssetYears[0]);
      }
    }
  };

  const toggleModalState = () => setModalMinimized((prevState) => !prevState);

  const toggleEVModalState = () =>
    setEVModalMinimized((prevState) => !prevState);

  const executeActionPerDataCategoryType = (
    modalData: AttributesPanelData,
    parseEyeViYears: {
      (years: EyeViDto[]): void;
      (arg0: EyeViDto[]): void;
    },
    parseModalDetails: { (modalDet: ModalDetailsState): void },
    refreshData: boolean = false,
  ) => {
    if (modalData.category === 'network') {
      assetActions()
        .getEyeViYears(refreshData)
        .then((result) => {
          parseEyeViYears(result);
          setAttributesRefreshLoading(false);
        })
        .catch(() => {
          setAttributesRefreshLoading(false);
          setDetailsUnavailableMessage('Unable to load EyeVi years');
          setIsLoading(false);
        });
    }

    if (!refreshData) {
      setIsLoading(true);
    }
    setDetailsUnavailableMessage(null);

    if (modalData.category === SDM_DEFECT_FEATURE_CATEGORY) {
      if (!isSdmFeatureInfoDetails(modalData)) {
        setModalDetails({});
        setShowAttributes(false);
        setShowElements(false);
        setShowAssetHub(false);
        setDetailsUnavailableMessage('No details for this feature');
        setIsLoading(false);
        if (refreshData) {
          setAttributesRefreshLoading(false);
          setWasAttributesRefreshedClicked(false);
        }
        return;
      }

      parseModalDetails(toSdmFeatureInfoRows(modalData));
      if (refreshData) {
        setAttributesRefreshLoading(false);
        setWasAttributesRefreshedClicked(false);
      }
      return;
    }

    // Category resolvers live in attributeResolvers.ts
    resolveAttributeDetails(modalData, refreshData)
      .then((result) => {
        if (!result) {
          setDetailsUnavailableMessage('No details for this feature');
          setModalDetails({});
          setShowAttributes(false);
          setShowElements(false);
          setShowAssetHub(false);
          setCurrentTab(0);
          setIsLoading(false);
          if (refreshData) {
            setAttributesRefreshLoading(false);
            setWasAttributesRefreshedClicked(false);
          }
          return;
        }
        parseModalDetails(result.details);
        if (result.category === 'asset') {
          setShowAssetHub(true);
        }
        if (refreshData) {
          setAttributesRefreshLoading(false);
          setWasAttributesRefreshedClicked(false);
        }
      })
      .catch(() => {
        setDetailsUnavailableMessage('Unable to load details');
        setModalDetails({});
        setShowAttributes(false);
        setShowElements(false);
        setShowAssetHub(false);
        setCurrentTab(0);
        setIsLoading(false);
        if (refreshData) {
          setAttributesRefreshLoading(false);
          setWasAttributesRefreshedClicked(false);
        }
      });
  };

  useEffect(() => {
    const onLayerRefresh = (event: Event) => {
      console.log(
        'Received layer-refresh event:',
        event,
        showAssetHub,
        data?.category,
      );
      if (showAssetHub && data?.category === 'asset') {
        setWasAssetHubRefreshedClicked(true);
        setAssetHubRefreshToken((token) => token + 1);
        setWasAssetHubRefreshLoading(true);
      }

      if (data && data.category) {
        setWasAttributesRefreshedClicked(true);
        setAttributesRefreshLoading(true);
        executeActionPerDataCategoryType(
          data,
          parseEyeViYears,
          parseModalDetails,
          true,
        );
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('layer-refresh', onLayerRefresh);
      window.addEventListener('legend-refresh', onLayerRefresh);
      return () => {
        window.removeEventListener('layer-refresh', onLayerRefresh);
        window.removeEventListener('legend-refresh', onLayerRefresh);
      };
    }
  }, [
    showAssetHub,
    data?.category,
    data,
    executeActionPerDataCategoryType,
    parseEyeViYears,
    parseModalDetails,
  ]);

  useEffect(() => {
    if (isOpen && data) {
      executeActionPerDataCategoryType(
        data,
        parseEyeViYears,
        parseModalDetails,
      );
    }
  }, [isOpen, data]);

  // Keep Selected Item above Survey Data Viewer / Survey Layers when opened or re-selected.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setZIndex(getNextWindowZIndex());

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (!target.closest('.attributes-selected-item-modal')) {
        return;
      }
      setZIndex(getNextWindowZIndex());
    };

    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [isOpen, data]);

  const onChangeSpan = (sp: string) => {
    setSelectedSpan(parseInt(sp));
  };

  const onChangeYear = (sp: string) => {
    setSelectedYear(parseInt(sp));
  };

  const onChangeAssetType = (sp: string) => {
    setSelectedAssetType(sp);
    const eyeViAssetYears = eyeViData
      .filter((x) => x.assetType === sp)
      .map((x) => x.year);
    setEyeViYears(eyeViAssetYears);
    if (eyeViAssetYears.length > 0) {
      setSelectedYear((prevYear) =>
        eyeViAssetYears.includes(prevYear) ? prevYear : eyeViAssetYears[0],
      );
    }
  };

  const onOpenEyeVi = () => {
    setIsLoading(true);
    setDetailsUnavailableMessage(null);
    assetActions()
      .getEyeViCredentials(selectedAssetType, selectedYear)
      .then((result) => {
        if (result) {
          setShowEyeVi(true);
          setIsExpanded(false);
          return;
        }
        setDetailsUnavailableMessage('Unable to open EyeVi');
      })
      .catch(() => {
        setDetailsUnavailableMessage('Unable to open EyeVi');
      })
      .finally(() => setIsLoading(false));
  };

  const closeEyeViModal = () => {
    setShowEyeVi(false);
    setEVModalMinimized(false);
  };

  const updateDimensions = useCallback(() => {
    const newNavbarWidth = isExpanded ? 27.5 : 4.8;
    setNavbarWidth(newNavbarWidth);

    const sidebarWidth = rightSidebarRef?.current?.offsetWidth || 300;
    setRightSidebarWidth(sidebarWidth);

    const minX = newNavbarWidth * 10;
    setModalPosition((prevPosition) =>
      prevPosition.x < minX
        ? { x: minX, y: prevPosition.y }
        : prevPosition,
    );
  }, [isExpanded, rightSidebarRef]);

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [updateDimensions]);

  const handleDragStart = useCallback((_e: DraggableEvent) => {
    setIsDragging(true);
  }, []);

  const handleDragStop = useCallback(
    (_e: DraggableEvent, dragData: DraggableData) => {
      const maxX = window.innerWidth - rightSidebarWidth - 480;
      const minX = (Number.isFinite(navbarWidth) ? navbarWidth : 0) * 10;
      setIsDragging(false);
      setModalPosition({
        x: clampAttributesModalPositionX(dragData.x, minX, maxX),
        y: dragData.y,
      });
    },
    [navbarWidth, rightSidebarWidth],
  );

  return (
    <>
      {showAssetHub && isAssetHubModalData(data) ? (
        <AssetHub
          closeModal={closeModal}
          data={data}
          zIndex={zIndex}
          initialDetails={
            isAssetAttributeModalDetails(modalDetails)
              ? modalDetails
              : undefined
          }
          wasAssetHubRefreshedClicked={wasAssetHubRefreshedClicked}
          setWasAssetHubRefreshedClicked={setWasAssetHubRefreshedClicked}
          assetHubRefreshToken={assetHubRefreshToken}
          setAssetHubRefreshToken={setAssetHubRefreshToken}
          wasAssetHubRefreshLoading={wasAssetHubRefreshLoading}
          setWasAssetHubRefreshLoading={setWasAssetHubRefreshLoading}
        />
      ) : (
        <div>
          <Modal
            ariaHideApp={false}
            isOpen={isOpen}
            bodyOpenClassName={null}
            portalClassName="react-modal-container"
            overlayClassName="react-modal-overlay"
            className="react-modal-wrapper"
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
          >
            <Draggable
              nodeRef={modalHeight}
              enableUserSelectHack={false}
              handle=".modal-header"
              cancel=".modal-header-buttons, .modal-refresh-button"
              position={isDragging ? undefined : modalPosition}
              onStart={handleDragStart}
              onStop={handleDragStop}
              bounds={{
                left: Number.isFinite(navbarWidth) ? navbarWidth : 0,
                top: 0,
                bottom:
                  window.innerHeight -
                  (modalHeight.current?.offsetHeight || 0) -
                  20,
                right: window.innerWidth - rightSidebarWidth - 480,
              }}
            >
              <div
                className={`react-modal top-left attributes-selected-item-modal${isModalMinimized ? ' minimized' : ''}`}
                ref={modalHeight}
                style={{ maxWidth: 'calc(100vw - 150.2rem)', zIndex }}
              >
                <div className="modal-header">
                  <div className="modal-title-container">
                    <div className="modal-title-in-container">
                      SELECTED ITEM
                    </div>
                    <FiRefreshCw
                      onClick={() => {
                        if (!data) {
                          return;
                        }
                        setWasAttributesRefreshedClicked(true);
                        setAttributesRefreshLoading(true);
                        executeActionPerDataCategoryType(
                          data,
                          parseEyeViYears,
                          parseModalDetails,
                          true,
                        );
                      }}
                      className={
                        attributesRefreshLoading &&
                        wasAttributesRefreshedClicked
                          ? 'modal-refresh-button rotating'
                          : 'modal-refresh-button'
                      }
                    />
                  </div>
                  <div className="modal-header-buttons">
                    <div className="modal-control" onClick={toggleModalState}>
                      {isModalMinimized ? (
                        <TbArrowsMaximize />
                      ) : (
                        <TbArrowsMinimize />
                      )}
                    </div>
                    <div
                      className="modal-close"
                      onClick={() => closeModal('Attributes')}
                    >
                      <MdClose />
                    </div>
                  </div>
                </div>
                <div className="modal-content">
                  {isLoading && <Loader />}
                  {!isLoading && detailsUnavailableMessage && (
                    <p className="xa-attributes-empty-message" role="status">
                      {detailsUnavailableMessage}
                    </p>
                  )}
                  {!isLoading && !detailsUnavailableMessage && (
                  <Tabs selectedIndex={currentTab} onSelect={changeTab}>
                    <TabList>
                      <Tab>Info</Tab>
                      {showAttributes && <Tab>Attributes</Tab>}
                      {showElements && <Tab>Elements</Tab>}
                    </TabList>

                    <TabPanel>
                      <div className="xa-attributes-list">
                        {/* List head */}
                        <div className="xa-attributes-list-head">
                          <div className="xa-attributes-list-label">Name</div>
                          <div className="xa-attributes-list-value-group">
                            <div className="xa-attributes-list-value">Value</div>
                          </div>
                        </div>

                        {/* List rows */}
                        {getModalDetailDisplayEntries(modalDetails).map(
                          ({ key, value }: ModalDetailDisplayEntry) => (
                            <AttributeRow
                              name={key}
                              value={value}
                              key={key}
                            />
                          ),
                        )}
                        {/* List rows */}
                      </div>
                    </TabPanel>

                    {showAttributes && (
                      <TabPanel>
                        <div className="xa-attributes-list">
                          {/* List head */}
                          <div className="xa-attributes-list-head">
                            <div className="xa-attributes-list-label">Name</div>
                            <div className="xa-attributes-list-value-group">
                              <div className="xa-attributes-list-value">Value</div>
                            </div>
                          </div>

                          {/* List rows */}
                          {attributes.map((att) => (
                            <AttributeRow
                              name={att.name}
                              value={att.value}
                              key={att.name}
                            />
                          ))}
                          {/* List rows */}
                        </div>
                      </TabPanel>
                    )}

                    {showElements && (
                      <TabPanel>
                        <div className="xa-attributes-elements">
                          <label htmlFor="groups" className="required">
                            Span
                          </label>
                          <select
                            name="groups"
                            id="groups"
                            onChange={(e) => onChangeSpan(e.target.value)}
                          >
                            {spans?.map((value) => (
                              <option
                                value={value}
                                key={value}
                                selected={selectedSpan === value}
                              >
                                {value}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="xa-attributes-list">
                          {/* List head */}
                          <div className="xa-attributes-list-head">
                            <div className="xa-attributes-list-label full-width">
                              Name
                            </div>
                          </div>

                          {/* List rows */}
                          {elements
                            .filter((x) => x.spanNumber === selectedSpan)
                            .map((el) => (
                              <ElementRow
                                name={el.name}
                                key={`${el.name}-${el.spanNumber}`}
                              />
                            ))}
                          {/* List rows */}
                        </div>
                      </TabPanel>
                    )}
                  </Tabs>
                  )}
                </div>

                <div className="modal-footer">
                  {!detailsUnavailableMessage &&
                    (eyeViYears || []).length > 0 &&
                    data?.category === 'network' && (
                      <>
                        <strong className="xa-eyevi-title">EyeVi</strong>
                        <div className="xa-eyevi-row">
                          <div>
                            <select
                              name="eyeViYears"
                              id="eyeViYears"
                              onChange={(e) => onChangeYear(e.target.value)}
                            >
                              {eyeViYears?.map((value) => (
                                <option
                                  value={value}
                                  key={value}
                                  selected={selectedYear === value}
                                >
                                  {value}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <select
                              name="eyeViAssetTypes"
                              id="eyeViAssetTypes"
                              onChange={(e) =>
                                onChangeAssetType(e.target.value)
                              }
                            >
                              {eyeViAssetTypes?.map((value) => (
                                <option
                                  value={value}
                                  key={value}
                                  selected={selectedAssetType === value}
                                >
                                  {value}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="xa-eyevi-btns">
                          <Button
                            onClick={onOpenEyeVi}
                            color="primary"
                            full
                            isButton
                          >
                            Open
                          </Button>
                        </div>
                      </>
                    )}
                </div>
              </div>
            </Draggable>
          </Modal>
          <Modal
            ariaHideApp={false}
            isOpen={showEyeVi}
            bodyOpenClassName={null}
            portalClassName="react-modal-container"
            overlayClassName="react-modal-overlay"
            className="react-modal-wrapper"
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
          >
            <div
              className={`react-modal top-left${isEVModalMinimized ? ' minimized' : ''}`}
            >
              <div className="modal-header">
                <div className="modal-title">EyeVi</div>
                <div className="modal-header-buttons">
                  <div className="modal-control" onClick={toggleEVModalState}>
                    {isEVModalMinimized ? (
                      <TbArrowsMaximize />
                    ) : (
                      <TbArrowsMinimize />
                    )}
                  </div>
                  <div
                    className="modal-close"
                    onClick={() => closeEyeViModal()}
                  >
                    <MdClose />
                  </div>
                </div>
              </div>
              <div className="modal-content">
                <Iframe
                  url={`${process.env.REACT_APP_EYEVI_URL}/dist/#${selectedEyeviYear}`}
                  width="2000"
                  height="2000"
                  id=""
                  className="eyevi-iframe"
                  display="block"
                  position="relative"
                />
              </div>
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};

export default Attributes;
